import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface EpisodeOption {
  episodeIndex: number;
  title?: string;
}

interface EpisodeMultiSelectProps {
  episodes: readonly EpisodeOption[];
  selected: readonly number[];
  onChange: (next: number[]) => void;
  disabled?: boolean;
  // Shown when the project has no episodes yet.
  emptyHint?: string;
}

// Search appears once the episode list exceeds this threshold, matching the
// plan's "短剧通常 ≤24 集, >20 加搜索" decision.
const SEARCH_THRESHOLD = 20;

const pad2 = (n: number): string => String(n).padStart(2, '0');

const EpisodeMultiSelect: React.FC<EpisodeMultiSelectProps> = ({
  episodes,
  selected,
  onChange,
  disabled = false,
  emptyHint,
}) => {
  const { t } = useTranslation('common');
  const [search, setSearch] = useState('');

  const resolvedEmptyHint = emptyHint ?? t('noEpisodesHint');

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const filtered = useMemo(() => {
    if (!search.trim()) return episodes;
    const q = search.trim().toLowerCase();
    return episodes.filter(ep => {
      const label = `ep${pad2(ep.episodeIndex)}`;
      if (label.includes(q)) return true;
      if (String(ep.episodeIndex).includes(q)) return true;
      if (ep.title && ep.title.toLowerCase().includes(q)) return true;
      return false;
    });
  }, [episodes, search]);

  const toggle = (idx: number) => {
    if (disabled) return;
    if (selectedSet.has(idx)) {
      onChange(selected.filter(i => i !== idx));
    } else {
      onChange([...selected, idx].sort((a, b) => a - b));
    }
  };

  const selectAll = () => {
    if (disabled) return;
    onChange(episodes.map(e => e.episodeIndex).sort((a, b) => a - b));
  };

  const clearAll = () => {
    if (disabled) return;
    onChange([]);
  };

  if (episodes.length === 0) {
    return <div style={{ color: '#999', fontSize: 13, padding: '6px 0' }}>{resolvedEmptyHint}</div>;
  }

  const showSearch = episodes.length > SEARCH_THRESHOLD;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 8,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ fontSize: 12, color: '#666' }}>
          {t('selected', { current: selected.length, total: episodes.length })}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={selectAll}
            disabled={disabled || selected.length === episodes.length}
            style={chipButtonStyle(false, disabled || selected.length === episodes.length)}
          >
            {t('selectAll')}
          </button>
          <button
            type="button"
            onClick={clearAll}
            disabled={disabled || selected.length === 0}
            style={chipButtonStyle(false, disabled || selected.length === 0)}
          >
            {t('clearAll')}
          </button>
        </div>
      </div>

      {showSearch && (
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('searchEpisodePlaceholder')}
          style={{
            width: '100%',
            padding: '6px 10px',
            fontSize: 13,
            border: '1px solid #ddd',
            borderRadius: 4,
            marginBottom: 8,
            boxSizing: 'border-box',
          }}
          disabled={disabled}
        />
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
          gap: 6,
          maxHeight: 240,
          overflowY: 'auto',
        }}
      >
        {filtered.map(ep => {
          const isSel = selectedSet.has(ep.episodeIndex);
          return (
            <button
              key={ep.episodeIndex}
              type="button"
              onClick={() => toggle(ep.episodeIndex)}
              disabled={disabled}
              title={ep.title || `EP${pad2(ep.episodeIndex)}`}
              style={chipButtonStyle(isSel, disabled)}
            >
              EP{pad2(ep.episodeIndex)}
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div style={{ gridColumn: '1 / -1', color: '#999', fontSize: 12, padding: 8 }}>
            {t('noMatch')}
          </div>
        )}
      </div>
    </div>
  );
};

function chipButtonStyle(active: boolean, disabled: boolean): React.CSSProperties {
  return {
    padding: '6px 10px',
    fontSize: 12,
    border: `1px solid ${active ? '#1677ff' : '#d9d9d9'}`,
    background: active ? '#e6f4ff' : '#fff',
    color: active ? '#1677ff' : '#333',
    borderRadius: 4,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition: 'background 0.15s, border-color 0.15s',
  };
}

export default EpisodeMultiSelect;
